// 마을②~⑥ 각자 지도(S7, docs/STAGE-WORLD-PRD.md 5.1; ⑤⑥은 3개월 C2). 마을①(levels.VILLAGE)을 복사하지 않고 판 모양·시설 자리·바깥 풍경이 다르다.
//   ② 잿불 전초기지 — 용암 바다 위 성채 섬. 한가운데 큰 대장간(화로·굴뚝), 용암 수로를 건너는 돌다리, 흑요석 성탑
//   ③ 서리 항구     — 눈 덮인 항구. 윗장터 → 부두 산책로 → 남쪽 잔교(배가 묶여 있다), 동쪽 끝 보석상, 등대
//   ④ 늪지 장터     — 늪 물 위 널판 데크 넷을 다리로 잇는다. 장터 고목, 동쪽 데크 연금술사·잡화상, 수상 가옥
//   ⑤ 오아시스 대상단 — 한가운데 청록 샘을 고리 모양 모래 광장이 두른다. 대상 천막 장터, 낙타 수레, 서쪽 대상로·남동쪽 사구길 끝에 문. 해질녘 금빛
//   ⑥ 하늘 요새     — 구름 바다 위 흰 돌 요새 섬. 가운데 수정 순간이동진 광장, 동쪽 비공정 선착장, 서쪽 대장간 테라스, 북동쪽 성채 문, 멀리 보라 성채
// 던전 문(2026-10-27, STAGE-WORLD-PRD 5.4): 마을마다 자리·방향이 다르다(② 서·동 둑길 끝에서 마주 봄, ③ 북서 언덕길·동쪽 잔교 끝,
// ④ 서쪽 널판 다리 끝 + 북쪽 황금 모래 폐도 문, ⑤ 서쪽 대상로 끝 동향 문 + 남동 사구길 끝 북향 문, ⑥ 북동 성채 문 + 서쪽 테라스 끝 준비 중 문). 시작 자리는 모두 남쪽 (0, 10)에서 북쪽을 본다. 순서 고정: exits[0] = 지금 지역, exits[1] = 다음 지역.
// 문(게이트 랜드마크)은 자기 출구 사각형 바로 뒤에 붙고, 정면은 걸을 수 있는 바닥 쪽(gateFront)이다. 자동 이동·문 앞 카드·퀘스트 창은
// 출구 사각형을 읽으므로 좌표가 달라도 그대로 맞는다(villageMaps.test가 확인).
// 게임 판정은 1m 격자(levels.rasterize)다. 물·용암·섬·집·세트피스는 렌더 전용 데이터(TOWN_LOOKS)이고 판정에 들어가지 않는다.
// 좌표: y 위, 바닥 XZ 평면, 북쪽(화면 위) = -z. three·DOM import 금지(공유 데이터).
import type { ExitDef, Landmark, MapDef, Rect, RegionId, SetPieceDef } from './levels';
import { cellsOf, chamfer, disk, union } from './shapes';

const r = (x0: number, z0: number, x1: number, z1: number): Rect => ({ x0, z0, x1, z1 });
const N = Math.PI; // facing: 북쪽(-z)
const E = Math.PI / 2; // NPC rot: 동쪽(+x)을 본다

// ── 마을 공통: 시작 자리·문 도우미 ───────────────
/** 시작 자리(남쪽 가운데, 북쪽을 본다). 마을①~④ 모두 같다(퀘스트 창 방위어의 기준점) */
export const TOWN_SPAWN = { x: 0, z: 10, facing: N } as const;

/** 던전 문 하나: 문 아치 사각형(1칸 두께) · 출구 사각형(복도 안, 문에 붙는다, 없으면 준비 중 문) */
interface DoorSpec { readonly gate: Rect; readonly exit: Rect | null; readonly to: RegionId | null; readonly label: string }
function doors(current: DoorSpec, next: DoorSpec): { landmarks: Landmark[]; exits: ExitDef[] } {
  const landmarks: Landmark[] = [current, next].map((d) => ({ kind: 'gate' as const, label: d.label, rect: d.gate, height: 6, solid: true }));
  // 순서 고정: exits[0] = 지금 지역, exits[1] = 다음 지역(출구가 없는 준비 중 문은 빠진다)
  const exits: ExitDef[] = [];
  for (const d of [current, next]) if (d.exit && d.to) exits.push({ rect: d.exit, to: d.to, label: d.label });
  return { landmarks, exits };
}

/**
 * 문(게이트 사각형)의 정면 방향(rad, 렌더 규약: 앞 벡터 = (sin r, cos r)). 사각형 네 변 바로 바깥 칸 중 걸을 수 있는 바닥이
 * 가장 많이 붙은 쪽이 정면이다: +z(남) 0 · +x(동) π/2 · −z(북) π · −x(서) −π/2. 바닥이 하나도 안 붙으면 0
 */
export function gateFront(floors: readonly Rect[], gate: Rect): number {
  const walk = cellsOf(floors);
  const count = (x0: number, z0: number, x1: number, z1: number): number => {
    let n = 0;
    for (let z = z0; z < z1; z++) for (let x = x0; x < x1; x++) if (walk.has(x + ',' + z)) n++;
    return n;
  };
  const sides: readonly [number, number][] = [
    [count(gate.x0, gate.z1, gate.x1, gate.z1 + 1), 0],
    [count(gate.x1, gate.z0, gate.x1 + 1, gate.z1), Math.PI / 2],
    [count(gate.x0, gate.z0 - 1, gate.x1, gate.z0), Math.PI],
    [count(gate.x0 - 1, gate.z0, gate.x0, gate.z1), -Math.PI / 2],
  ];
  let best = sides[0];
  for (const sd of sides) if (sd[0] > best[0]) best = sd;
  return best[0] > 0 ? best[1] : 0;
}

// ── NPC 자리(마을별). 역할 → 서 있는 자리·바라보는 방향. 시설 문 앞, 걸을 수 있는 바닥(villageMaps.test가 확인) ──
export type TownNpcRole = 'shop' | 'forge' | 'trainer' | 'jeweler' | 'alchemist' | 'raid';
export interface TownNpcSpot { readonly x: number; readonly z: number; readonly rot: number }
/** 마을 번호(2..6) → 역할별 자리. 마을①은 shared/rpg VILLAGE_NPCS의 자리 그대로. 보석상은 ③, 연금술사는 ④에만 있다 */
export const TOWN_NPC_SPOTS: Readonly<Record<number, Readonly<Partial<Record<TownNpcRole, TownNpcSpot>>>>> = {
  // raid = 레이드 지휘관(RAID-PRD 1절, 마을②부터): 광장 열린 바닥, 다른 NPC·출발점에서 5m 이상
  2: { forge: { x: 5, z: -1.9, rot: 0 }, shop: { x: 21.4, z: -6.5, rot: -E }, trainer: { x: -24.4, z: -5.5, rot: E }, raid: { x: -12.5, z: -4.5, rot: E } },
  3: { shop: { x: -10.6, z: -7.5, rot: E }, forge: { x: 11.4, z: -7.4, rot: -E }, trainer: { x: -16.4, z: 2, rot: E }, jeweler: { x: 24.4, z: 1.5, rot: -E }, raid: { x: 8.5, z: -0.5, rot: -E } },
  4: { forge: { x: -5.5, z: -5.4, rot: 0 }, shop: { x: 27.4, z: -7, rot: -E }, alchemist: { x: 27.4, z: 0, rot: -E }, trainer: { x: -19.4, z: -5, rot: E }, raid: { x: 13.5, z: -4.5, rot: -E } },
  // ⑤ 건물은 길 가장자리 바깥에 서고 NPC는 건물을 등지고 광장을 본다. 레이드 지휘관은 시작 자리에서 5m 넘게 떨어진 남쪽 광장
  5: { forge: { x: 1, z: -15.4, rot: 0 }, shop: { x: 13.4, z: -5.5, rot: -E }, trainer: { x: -24, z: -1.4, rot: 0 }, raid: { x: -4.5, z: 6.5, rot: 0 } },
  6: { forge: { x: -14.5, z: -7.4, rot: 0 }, shop: { x: -17.5, z: -0.4, rot: N }, trainer: { x: 16, z: -3.4, rot: 0 }, raid: { x: -4.2, z: 6.2, rot: 0 } },
};

// ── 렌더 전용 풍경 데이터 ──────────────────────────────
/** 판 바깥을 채우는 액체: 용암 바다 · 겨울 바다 · 늪 물 · 오아시스 청록 샘물 · 구름 바다 */
export type TownLiquid = 'lava' | 'sea' | 'swamp' | 'oasis' | 'cloud';
/** 마을 전용 Blender 세트피스(public/assets/town/town_<이름>.glb, tools/blender/town_sets.py) */
export type TownSetName = 'great_forge' | 'obsidian_spire' | 'ship' | 'lighthouse' | 'stilt_house' | 'cauldron';
export const TOWN_SET_NAMES: readonly TownSetName[] = ['great_forge', 'obsidian_spire', 'ship', 'lighthouse', 'stilt_house', 'cauldron'];
/** 키트 소품 종류(렌더 env/kits.ts KitKind 중 마을이 쓰는 것) */
export type TownPropKind =
  | 'v_house' | 'village_house_b' | 'v_lamppost' | 'village_market_stall' | 'village_banner_pole' | 'village_well' | 'village_flower_planter'
  | 'crates' | 'brazier' | 'banner' | 'pillar' | 'tree' | 'rock' | 'rock_lo' | 'rubble' | 'statue' | 'mushrooms' | 'tree_lo';
export interface TownProp {
  readonly kind: TownPropKind;
  readonly x: number;
  readonly z: number;
  /** 정면(+z)이 향할 방향(rad) */
  readonly rot: number;
  /** 높이(m, 균일 배율) */
  readonly h: number;
  /** 인스턴스 색 곱(기본 흰색) */
  readonly tint?: number;
  /** 바닥 위 높이(m) */
  readonly y?: number;
}
export interface TownSetDef {
  readonly kind: TownSetName;
  readonly x: number;
  readonly z: number;
  readonly rot: number;
  readonly scale?: number;
  readonly y?: number;
  /** 이 조각이 대신 그리는 랜드마크 인덱스(그레이박스를 숨긴다) */
  readonly landmark?: number;
  /** 색 곱(기본: 조각 종류별 규칙) */
  readonly tint?: number;
  /** true면 조각 종류의 기본 불빛(불꽃·망울)을 켜지 않는다(원경 장식용) */
  readonly noLight?: boolean;
}
export interface TownLook {
  readonly town: number;
  readonly liquid: TownLiquid;
  /** 액체 면 높이(m, 음수) */
  readonly liquidY: number;
  /** 바닥 판석 텍스처(public/assets 상대). 바닥 규칙은 stageSurfaces 키 */
  readonly floorTex: string;
  readonly floorKey: 'a' | 'b' | 'm' | 'f';
  /** 원경 파노라마 */
  readonly pano: string;
  /** 섬(렌더 전용 땅): 걸을 수 없는 집 터. 걸을 수 있는 바닥과 2칸 이상 떨어져 물·용암 띠로 갈린다 */
  readonly islands: readonly Rect[];
  /** 섬 위 집 배치 규칙: 간격(m)·높이·색 곱 */
  readonly houses: { readonly spacing: number; readonly h: readonly [number, number]; readonly tint: number };
  /** 바닥 가장자리 풀 카드 종류 */
  readonly cards: 'tuft' | 'frost' | 'reed';
  /** 대기 입자 */
  readonly drift: 'snow' | 'spore' | null;
  /** 키트 소품(가로등·노점·상자·화로·나무 등) */
  readonly props: readonly TownProp[];
  /** 마을 전용 Blender 세트피스 */
  readonly townSets: readonly TownSetDef[];
  /** 추가 불빛 망울(수정·등불·샘). 색은 마을 공통(townEnv GLOW_RGB) */
  readonly lights?: readonly { readonly x: number; readonly y: number; readonly z: number; readonly s: number }[];
  /** 장식 배우(의뢰인 등, 상호작용 없음): 자리·방향 */
  readonly extras: readonly { readonly x: number; readonly z: number; readonly rot: number }[];
}

// ════════════════════════════════════════════════════════
// ② 잿불 전초기지
// ════════════════════════════════════════════════════════
const T2_FLOORS = union(
  r(-24, -18, -6, -12), // 서쪽 둑길(끝에 잿불 협곡 문, 정면 동쪽)
  r(-6, -16, 16, -12), // 성문 마당(두 둑길을 잇는다)
  r(16, -18, 30, -12), // 동쪽 둑길(끝에 서리 묘지 문, 정면 서쪽 — 두 문이 마주 본다)
  chamfer(r(-13, -12, 17, 2), 5, ['sw', 'se']), // 대장간 광장(남쪽 모서리를 크게 깎은 팔각)
  r(-26, -9, -13, -2), // 서쪽 보루 다리길(교관)
  r(17, -10, 23, -3), // 동쪽 보루(상점)
  r(-2, 2, 2, 6), // 용암 수로를 건너는 돌다리
  chamfer(r(-8, 6, 8, 18), 4, ['nw', 'ne', 'sw', 'se']), // 남쪽 성문 뜰(시작 자리)
);
const T2_DOORS = doors(
  { gate: r(-25, -19, -24, -11), exit: r(-24, -18, -21, -12), to: 'r2', label: '잿불 협곡' },
  { gate: r(30, -19, 31, -11), exit: r(27, -18, 30, -12), to: 'r3', label: '서리 묘지' },
);
const T2_LANDMARKS: Landmark[] = [
  { kind: 'forge', label: '대장간', rect: r(1, -9, 9, -3), height: 7, solid: true }, // 0: 광장 한가운데 큰 대장간(Blender great_forge)
  { kind: 'shop', label: '상점', rect: r(23, -10, 29, -3), height: 5, solid: true }, // 1
  { kind: 'trainer', label: '교관', rect: r(-32, -9, -26, -2), height: 4, solid: true }, // 2
  { kind: 'board', label: '의뢰 게시판', rect: r(4, 12, 7, 13), height: 2.4, solid: true }, // 3
  ...T2_DOORS.landmarks, // 4·5
  // 흑요석 노점(광장 남쪽 가장자리 양옆, 다리 길은 비운다)
  { kind: 'stall', rect: r(-11, -6, -8, -3), height: 2.4, solid: true }, // 6
  { kind: 'stall', rect: r(10, -2, 13, 1), height: 2.4, solid: true }, // 7
  // 성문 뜰 앞 전경(가로등·깃대), 시작 자리 남쪽이라 동선을 막지 않는다
  { kind: 'decor', rect: r(-5, 14, -4, 16), height: 3.6, solid: true }, // 8
  { kind: 'decor', rect: r(4, 15, 5, 17), height: 3.6, solid: true }, // 9
];
const T2: MapDef = {
  id: 'village',
  name: '잿불 전초기지',
  floors: T2_FLOORS,
  landmarks: T2_LANDMARKS,
  gates: [],
  rooms: [],
  exits: T2_DOORS.exits,
  spawn: TOWN_SPAWN,
  wallHeight: 2.5,
  // 가장자리: 성채 흉벽(낮은 돌 난간), 돌다리 자리는 다리 조각이 대신한다
  edgeStyle: 'parapet',
  edgeZones: [{ rect: r(-2, 2, 2, 6), style: 'bare' }],
  sets: [
    { kind: 'bridge', x: 0, z: 4, rot: 0, len: 4.6 },
    // 원경: 북쪽·동서 화산 절벽(용암 바다 너머)
    { kind: 'far_cliff', x: -46, z: -40, rot: 0.5, scale: 1.4, y: -2 },
    { kind: 'far_cliff', x: 52, z: -36, rot: -0.6, scale: 1.5, y: -2 },
    { kind: 'far_cliff', x: 4, z: -62, rot: 0, scale: 1.8, y: -3 },
  ] as SetPieceDef[],
};
const T2_LOOK: TownLook = {
  town: 2,
  liquid: 'lava',
  liquidY: -1.3,
  floorTex: 'town/tex/floor_obsidian.webp',
  floorKey: 'm',
  pano: 'village/tex/bg_village_panorama.webp',
  // 둑길 북쪽 섬은 둑길과 2칸 이상 띄우고, 서문·동문 바로 뒤는 비워 문이 용암 바다를 등지게 한다
  islands: [r(-32, -30, -8, -20), r(16, -30, 36, -20), r(-40, -16, -31, -10), r(-38, 0, -24, 10), r(-26, 18, -12, 28), r(12, 20, 28, 28), r(34, -8, 41, 8)],
  houses: { spacing: 7.5, h: [6.0, 7.4], tint: 0x9a7f7a },
  cards: 'tuft',
  drift: null,
  props: [
    // 대장간 앞 작업장: 상자·화로(불꽃)
    { kind: 'crates', x: 10.5, z: -7.5, rot: 0.3, h: 1.0 },
    { kind: 'brazier', x: -0.4, z: -2.6, rot: 0, h: 1.6 },
    { kind: 'brazier', x: 10.4, z: -2.6, rot: 0, h: 1.6 },
    // 다리 양끝 화로(용암 수로를 지키는 불)
    { kind: 'brazier', x: -3.2, z: 1.2, rot: 0, h: 1.7 },
    { kind: 'brazier', x: 3.2, z: 1.2, rot: 0, h: 1.7 },
    { kind: 'brazier', x: -3.2, z: 6.8, rot: 0, h: 1.7 },
    { kind: 'brazier', x: 3.2, z: 6.8, rot: 0, h: 1.7 },
    // 성문 마당 깃발(붉은 기)
    { kind: 'banner', x: -4.8, z: -15.2, rot: 0, h: 3.8, tint: 0xff9a70 },
    { kind: 'banner', x: 14.8, z: -15.2, rot: 0, h: 3.8, tint: 0xff9a70 },
    { kind: 'banner', x: 5.5, z: -15.2, rot: 0, h: 3.8, tint: 0xff9a70 },
    // 광장 가로등(검은 쇠)
    { kind: 'v_lamppost', x: -8, z: -10.6, rot: 0, h: 3.8, tint: 0x8c7a74 },
    { kind: 'v_lamppost', x: 16.4, z: -10.8, rot: 0, h: 3.8, tint: 0x8c7a74 },
    { kind: 'v_lamppost', x: -12.4, z: -2.6, rot: 0, h: 3.8, tint: 0x8c7a74 },
    { kind: 'v_lamppost', x: -7.4, z: 12, rot: 0, h: 3.6, tint: 0x8c7a74 },
    { kind: 'v_lamppost', x: 7.4, z: 12, rot: 0, h: 3.6, tint: 0x8c7a74 },
    // 교관 보루: 훈련 허수아비 대신 상자·깃대
    { kind: 'village_banner_pole', x: -21.4, z: -8.4, rot: E, h: 4.2, tint: 0xd08a70 },
    { kind: 'crates', x: -17.4, z: -2.6, rot: -0.2, h: 0.9 },
    { kind: 'village_banner_pole', x: 19.6, z: -9.4, rot: -E, h: 4.2, tint: 0xd08a70 },
    // 성문 뜰 상자 더미
    { kind: 'crates', x: -6.4, z: 9.6, rot: 0.4, h: 1.1 },
    { kind: 'crates', x: 6.2, z: 8.8, rot: -0.3, h: 0.95 },
    // 섬 둘레 바위(용암에서 솟은 현무암)
    { kind: 'rock', x: -12, z: 22, rot: 1.1, h: 2.6, tint: 0x5a4c50, y: -1.2 },
    { kind: 'rock', x: 12, z: 21, rot: 2.3, h: 2.2, tint: 0x5a4c50, y: -1.2 },
    { kind: 'rock', x: 25, z: 12, rot: 0.2, h: 2.8, tint: 0x5a4c50, y: -1.2 },
    { kind: 'rock', x: -26, z: 14, rot: 2.9, h: 2.4, tint: 0x5a4c50, y: -1.2 },
  ],
  townSets: [
    { kind: 'great_forge', x: 5, z: -6, rot: 0, landmark: 0 },
    // 흑요석 성탑(섬·바위 위, 잿불 빛)
    { kind: 'obsidian_spire', x: -14, z: -22, rot: 0.2 },
    { kind: 'obsidian_spire', x: 24, z: -24, rot: -0.4 },
    { kind: 'obsidian_spire', x: -31, z: 5, rot: 1.0, scale: 0.9 },
    { kind: 'obsidian_spire', x: 37, z: 2, rot: -1.2, scale: 0.9 },
    { kind: 'obsidian_spire', x: -20, z: 22, rot: 0.6, scale: 0.85 },
    { kind: 'obsidian_spire', x: 22, z: 22, rot: -0.7, scale: 0.85 },
  ],
  extras: [{ x: 8.4, z: 13.4, rot: -0.4 }],
};

// ════════════════════════════════════════════════════════
// ③ 서리 항구
// ════════════════════════════════════════════════════════
const T3_FLOORS = union(
  r(-10, -25, -4, -12), // 북서 언덕길(윗장터 북서 모서리에서 올라간다, 끝에 서리 묘지 문)
  chamfer(r(-12, -12, 13, -1), 3, ['nw', 'ne']), // 윗장터
  r(-18, -1, 26, 5), // 부두 산책로(동서로 길다)
  r(-3, 5, 3, 22), // 가운데 잔교(시작 자리, 배가 묶여 있다)
  r(-15, 5, -11, 14), // 서쪽 고깃배 잔교
  r(17, 5, 23, 19), // 동쪽 짐 부리는 턱 → 남쪽으로 늘린 잔교(끝에 가라앉은 사원 문, 정면 북쪽)
);
const T3_DOORS = doors(
  { gate: r(-11, -26, -3, -25), exit: r(-10, -25, -4, -22), to: 'r3', label: '서리 묘지' },
  { gate: r(16, 19, 24, 20), exit: r(17, 16, 23, 19), to: 'r4', label: '가라앉은 사원' },
);
const T3_LANDMARKS: Landmark[] = [
  { kind: 'shop', label: '상점', rect: r(-19, -11, -12, -4), height: 5, solid: true }, // 0
  { kind: 'forge', label: '대장간', rect: r(13, -11, 19, -5), height: 5, solid: true }, // 1
  { kind: 'trainer', label: '교관', rect: r(-24, -1, -18, 5), height: 4, solid: true }, // 2
  { kind: 'jeweler', label: '보석상', rect: r(26, -3, 32, 4), height: 5, solid: true }, // 3
  { kind: 'board', label: '의뢰 게시판', rect: r(5, 3, 8, 4), height: 2.4, solid: true }, // 4
  ...T3_DOORS.landmarks, // 5·6
  // 윗장터 등대(Blender lighthouse): 윗장터 가운데, 북서 언덕길(x −10..−4) 입구와 떨어져 길을 막지 않는다
  { kind: 'statue', label: '등대', rect: r(4, -9, 7, -6), height: 9, solid: true }, // 7
  // 장터 노점(윗장터·산책로 가장자리)
  { kind: 'stall', rect: r(-11, -4, -8, -1), height: 2.4, solid: true }, // 8
  { kind: 'stall', rect: r(13, 1, 16, 4), height: 2.4, solid: true }, // 9
];
const T3: MapDef = {
  id: 'village',
  name: '서리 항구',
  floors: T3_FLOORS,
  landmarks: T3_LANDMARKS,
  gates: [],
  rooms: [],
  exits: T3_DOORS.exits,
  spawn: TOWN_SPAWN,
  wallHeight: 2.5,
  // 가장자리: 돌 부두(둑), 바깥은 겨울 바다
  edgeStyle: 'water',
  sets: [
    { kind: 'far_cliff', x: -50, z: -44, rot: 0.6, scale: 1.6, y: -3 },
    { kind: 'far_cliff', x: 48, z: -46, rot: -0.5, scale: 1.7, y: -3 },
    { kind: 'far_cliff', x: 0, z: -66, rot: 0, scale: 2.0, y: -4 },
  ] as SetPieceDef[],
};
const T3_LOOK: TownLook = {
  town: 3,
  liquid: 'sea',
  liquidY: -1.0,
  floorTex: 'r3/tex/floor_snow_stone.webp',
  floorKey: 'b',
  pano: 'r3/tex/bg_r3_panorama.webp',
  // 북쪽 섬은 북서 언덕길 양옆으로(길과 2칸 이상), 옛 성문 마당 자리는 눈 섬 마을
  islands: [r(-34, -30, -12, -18), r(-2, -30, 16, -16), r(18, -30, 38, -14), r(-36, -14, -22, -3), r(-34, 9, -19, 20), r(27, 8, 40, 20), r(-30, 24, -14, 32)],
  houses: { spacing: 7.0, h: [5.8, 7.2], tint: 0xe2ecf8 },
  cards: 'frost',
  drift: 'snow',
  props: [
    // 부두 가장자리 상자·통(짐 부리는 곳)
    { kind: 'crates', x: 22.4, z: 10.6, rot: 0.2, h: 1.1 },
    { kind: 'crates', x: 17.6, z: 7.4, rot: -0.4, h: 0.9 },
    { kind: 'crates', x: -13.6, z: 12.6, rot: 0.5, h: 0.9 },
    { kind: 'crates', x: 1.9, z: 20.4, rot: 0.1, h: 0.85 },
    // 잔교 끝 계선주(짧은 기둥)
    { kind: 'pillar', x: -2.3, z: 21.3, rot: 0, h: 1.1, tint: 0x8a7a68 },
    { kind: 'pillar', x: 2.3, z: 15.2, rot: 0, h: 1.1, tint: 0x8a7a68 },
    { kind: 'pillar', x: 2.3, z: 7.6, rot: 0, h: 1.1, tint: 0x8a7a68 },
    // 동쪽 잔교(가라앉은 사원 문 길) 계선주
    { kind: 'pillar', x: 17.4, z: 14.4, rot: 0, h: 1.1, tint: 0x8a7a68 },
    { kind: 'pillar', x: 22.6, z: 14.4, rot: 0, h: 1.1, tint: 0x8a7a68 },
    // 산책로 가로등(푸른 불)
    { kind: 'v_lamppost', x: -6, z: 4.4, rot: 0, h: 3.8 },
    { kind: 'v_lamppost', x: 10, z: 4.4, rot: 0, h: 3.8 },
    { kind: 'v_lamppost', x: -17.2, z: -0.4, rot: 0, h: 3.8 },
    { kind: 'v_lamppost', x: 25.4, z: -0.4, rot: 0, h: 3.8 },
    { kind: 'v_lamppost', x: -3.6, z: -11.2, rot: 0, h: 3.8 },
    { kind: 'v_lamppost', x: 10.4, z: -10.6, rot: 0, h: 3.8 },
    // 북서 언덕길 푸른 깃발(길 양옆)
    { kind: 'banner', x: -9.4, z: -16.5, rot: 0, h: 3.8, tint: 0x9cc4ff },
    { kind: 'banner', x: -4.6, z: -16.5, rot: 0, h: 3.8, tint: 0x9cc4ff },
    // 윗장터 눈 덮인 나무
    { kind: 'tree', x: -9.6, z: -10.4, rot: 0.4, h: 6.4 },
    { kind: 'tree', x: 9.6, z: -3.6, rot: 1.9, h: 5.8 },
    // 바다의 얼음 바위
    { kind: 'rock', x: -8, z: 24, rot: 0.9, h: 2.2, y: -1.0, tint: 0xd8e6f6 },
    { kind: 'rock', x: 16, z: 26, rot: 2.1, h: 2.6, y: -1.0, tint: 0xd8e6f6 },
    { kind: 'rock', x: -24, z: 9, rot: 0.3, h: 1.8, y: -1.0, tint: 0xd8e6f6 },
  ],
  townSets: [
    { kind: 'ship', x: 8.8, z: 15, rot: 0 },
    { kind: 'lighthouse', x: 5.5, z: -7.5, rot: 0.3, landmark: 7 },
  ],
  extras: [{ x: 9.4, z: 3.4, rot: -0.6 }],
};

// ════════════════════════════════════════════════════════
// ④ 늪지 장터
// ════════════════════════════════════════════════════════
const T4_FLOORS = union(
  r(2, -22, 8, -16), // 북쪽 가운데 짧은 복도(끝에 황금 모래 폐도 문, 정면 남쪽)
  r(-6, -16, 16, -11), // 성문 데크
  disk(-3, -5, 6.5), // 북서 장터 데크(대장간)
  disk(11, -6, 5), // 북동 장터 데크(장터 고목)
  r(2, -6, 7, -3), // 두 장터 데크 사이 널판 다리
  r(-1, 1, 2, 6), // 남쪽 널판 다리
  disk(0, 11, 5.5), // 시작 데크(둥근 널판)
  r(15, -5, 21, -2), // 동쪽 널판 다리
  r(20, -10, 29, 3), // 동쪽 데크(잡화상·연금술사)
  r(-13, -5, -8, -2), // 서쪽 널판 다리
  r(-21, -8, -12, 3), // 서쪽 데크(교관·게시판)
  r(-21, 3, -18, 8), // 서쪽 데크에서 남쪽 널판 다리
  r(-22, 8, -16, 13), // 문 데크(끝에 가라앉은 사원 문, 정면 북쪽)
);
const T4_DOORS = doors(
  { gate: r(-23, 13, -15, 14), exit: r(-22, 10, -16, 13), to: 'r4', label: '가라앉은 사원' },
  { gate: r(1, -23, 9, -22), exit: r(2, -22, 8, -19), to: 'r5', label: '황금 모래 폐도' },
);
const T4_LANDMARKS: Landmark[] = [
  { kind: 'forge', label: '대장간', rect: r(-8, -12, -3, -7), height: 5, solid: true }, // 0
  { kind: 'shop', label: '상점', rect: r(29, -10, 35, -4), height: 5, solid: true }, // 1
  { kind: 'alchemist', label: '연금술사', rect: r(29, -3, 35, 3), height: 5, solid: true }, // 2
  { kind: 'trainer', label: '교관', rect: r(-27, -8, -21, -2), height: 4, solid: true }, // 3
  { kind: 'board', label: '의뢰 게시판', rect: r(-17, 1, -14, 2), height: 2.4, solid: true }, // 4
  ...T4_DOORS.landmarks, // 5·6
  // 장터 고목(북동 데크 가운데, 큰 나무 한 그루)
  { kind: 'tree', rect: r(10, -8, 12, -6), height: 9, solid: true }, // 7
  // 장터 노점(북서 데크 남쪽)
  { kind: 'stall', rect: r(-8, -2, -5, 1), height: 2.4, solid: true }, // 8
  // 연금술사 가마솥(Blender cauldron) 자리
  { kind: 'prop', rect: r(24, 1, 26, 3), height: 1.4, solid: true }, // 9
];
const T4: MapDef = {
  id: 'village',
  name: '늪지 장터',
  floors: T4_FLOORS,
  landmarks: T4_LANDMARKS,
  gates: [],
  rooms: [],
  exits: T4_DOORS.exits,
  spawn: TOWN_SPAWN,
  wallHeight: 2.5,
  // 가장자리: 늪 뿌리, 다리 자리는 널판 다리 조각이 대신한다
  edgeStyle: 'roots',
  edgeZones: [
    { rect: r(-1, 2, 2, 6), style: 'bare' },
    { rect: r(3, -6, 6, -3), style: 'bare' },
    { rect: r(16, -5, 20, -2), style: 'bare' },
    { rect: r(-13, -5, -9, -2), style: 'bare' },
    { rect: r(-21, 3, -18, 8), style: 'bare' },
  ],
  sets: [
    { kind: 'bridge_wood', x: 0.5, z: 4, rot: 0, len: 4.4 },
    { kind: 'bridge_wood', x: 4.5, z: -4.5, rot: E, len: 3.4 },
    { kind: 'bridge_wood', x: 18, z: -3.5, rot: E, len: 5.4 },
    { kind: 'bridge_wood', x: -11, z: -3.5, rot: E, len: 5.4 },
    { kind: 'bridge_wood', x: -19.5, z: 5.5, rot: 0, len: 5.4 },
    { kind: 'far_cliff', x: -48, z: -44, rot: 0.5, scale: 1.4, y: -3 },
    { kind: 'far_cliff', x: 50, z: -42, rot: -0.6, scale: 1.5, y: -3 },
  ] as SetPieceDef[],
};
const T4_LOOK: TownLook = {
  town: 4,
  liquid: 'swamp',
  liquidY: -0.9,
  floorTex: 'town/tex/floor_plank_deck.webp',
  floorKey: 'b',
  pano: 'r4/tex/bg_r4_panorama.webp',
  islands: [],
  houses: { spacing: 0, h: [0, 0], tint: 0xffffff },
  cards: 'reed',
  drift: 'spore',
  props: [
    // 데크 등불·상자·버섯
    { kind: 'v_lamppost', x: -4.6, z: -11.6, rot: 0, h: 3.6, tint: 0xb8c8a0 },
    { kind: 'v_lamppost', x: 14.6, z: -11.6, rot: 0, h: 3.6, tint: 0xb8c8a0 },
    { kind: 'v_lamppost', x: -3.6, z: 14.4, rot: 0, h: 3.4, tint: 0xb8c8a0 },
    { kind: 'v_lamppost', x: 3.6, z: 14.4, rot: 0, h: 3.4, tint: 0xb8c8a0 },
    { kind: 'v_lamppost', x: 21, z: 2.2, rot: 0, h: 3.4, tint: 0xb8c8a0 },
    { kind: 'v_lamppost', x: -20.2, z: -7.2, rot: 0, h: 3.4, tint: 0xb8c8a0 },
    { kind: 'crates', x: 21.4, z: -8.8, rot: 0.3, h: 0.95 },
    { kind: 'crates', x: -13.2, z: -7, rot: -0.2, h: 0.9 },
    { kind: 'crates', x: 3.4, z: 8.2, rot: 0.6, h: 0.85 },
    { kind: 'mushrooms', x: -3.6, z: 7.8, rot: 0.2, h: 0.9 },
    { kind: 'mushrooms', x: 8.2, z: -9.2, rot: 1.2, h: 0.8 },
    { kind: 'mushrooms', x: -20.2, z: 2.2, rot: 2.2, h: 0.85 },
    // 성문 데크 깃대(북쪽 준비 중 문 길 양옆)
    { kind: 'village_banner_pole', x: 0.5, z: -13.4, rot: 0, h: 4.0, tint: 0xb0d090 },
    { kind: 'village_banner_pole', x: 9.5, z: -13.4, rot: 0, h: 4.0, tint: 0xb0d090 },
    // 문 데크 등불(가라앉은 사원 문 앞)
    { kind: 'v_lamppost', x: -16.6, z: 8.6, rot: 0, h: 3.4, tint: 0xb8c8a0 },
    // 물 위 고목·바위(늪)
    { kind: 'tree', x: -11, z: 17, rot: 0.8, h: 8.5, y: -0.9 },
    { kind: 'tree', x: 16, z: 14, rot: 2.4, h: 7.8, y: -0.9 },
    { kind: 'tree', x: -30, z: -14, rot: 1.5, h: 8.2, y: -0.9 },
    { kind: 'tree', x: 36, z: -16, rot: 0.4, h: 8.0, y: -0.9 },
    { kind: 'rock', x: 9, z: 12, rot: 0.6, h: 1.4, y: -0.9 },
    { kind: 'rock', x: -10, z: 10, rot: 2.6, h: 1.2, y: -0.9 },
  ],
  townSets: [
    { kind: 'cauldron', x: 25, z: 2, rot: -E, landmark: 9 },
    // 수상 가옥(데크 둘레 물 위)
    { kind: 'stilt_house', x: -14, z: -18, rot: 0.3 },
    { kind: 'stilt_house', x: 24, z: -19, rot: -0.3 },
    { kind: 'stilt_house', x: -30, z: 11, rot: 2.4 },
    { kind: 'stilt_house', x: 25, z: 12, rot: -2.5 },
    { kind: 'stilt_house', x: 40, z: -2, rot: -E, scale: 0.9 },
    { kind: 'stilt_house', x: -32, z: -6, rot: E, scale: 0.9 },
    { kind: 'stilt_house', x: -10, z: 20, rot: 2.9, scale: 0.85 },
    { kind: 'stilt_house', x: 12, z: 21, rot: -2.9, scale: 0.85 },
  ],
  extras: [{ x: -12.8, z: 1.6, rot: -0.5 }],
};

// ════════════════════════════════════════════════════════
// ⑤ 오아시스 대상단 (3개월 C2)
// ════════════════════════════════════════════════════════
const T5_FLOORS = union(
  chamfer(r(-8, 2, 8, 18), 4, ['sw', 'se']), // 남쪽 광장(시작 자리)
  r(-15, -11, -5, 3), // 서쪽 천막 거리
  r(5, -11, 15, 3), // 동쪽 천막 거리(청록 샘을 사이에 두고 서쪽과 마주 본다)
  r(-9, -17, 9, -11), // 북쪽 대장간 마당
  r(-33, -3, -15, 3), // 서쪽 대상로(끝에 황금 모래 폐도 문, 정면 동쪽)
  r(6, 8, 22, 15), // 남동 사구길
  r(16, 15, 22, 27), // 사구길을 남쪽으로 늘린 끝(심연의 성채 문, 정면 북쪽)
);
const T5_DOORS = doors(
  { gate: r(-34, -4, -33, 4), exit: r(-33, -3, -30, 3), to: 'r5', label: '황금 모래 폐도' },
  { gate: r(15, 27, 23, 28), exit: r(16, 24, 22, 27), to: 'r6', label: '심연의 성채' },
);
const T5_LANDMARKS: Landmark[] = [
  { kind: 'shop', label: '상점', rect: r(15, -9, 21, -2), height: 5, solid: true }, // 0: 물 상인 천막집(동쪽 거리 끝)
  { kind: 'forge', label: '대장간', rect: r(-3, -24, 5, -17), height: 5, solid: true }, // 1: 천막 대장간(북쪽 마당 위)
  { kind: 'trainer', label: '교관', rect: r(-27, -10, -21, -3), height: 4, solid: true }, // 2: 호위대 막사(서쪽 대상로 북쪽)
  { kind: 'board', label: '의뢰 게시판', rect: r(-7, 12, -4, 13), height: 2.4, solid: true }, // 3
  ...T5_DOORS.landmarks, // 4·5
  // 대상 천막 장터(양쪽 거리 안쪽·북쪽 마당 끝)
  { kind: 'stall', rect: r(-14, -1, -11, 2), height: 2.4, solid: true }, // 6
  { kind: 'stall', rect: r(10, -1, 13, 2), height: 2.4, solid: true }, // 7
  { kind: 'stall', rect: r(5, -16, 8, -13), height: 2.4, solid: true }, // 8
];
const T5: MapDef = {
  id: 'village',
  name: '오아시스 대상단',
  floors: T5_FLOORS,
  landmarks: T5_LANDMARKS,
  gates: [],
  rooms: [],
  exits: T5_DOORS.exits,
  spawn: TOWN_SPAWN,
  wallHeight: 2.5,
  // 가장자리: 샘 둘레와 바깥 모두 돌 둑(오아시스를 두른 사암 가장자리)
  edgeStyle: 'water',
  sets: [
    // 원경: 사구 능선
    { kind: 'far_cliff', x: -50, z: -44, rot: 0.5, scale: 1.5, y: -3 },
    { kind: 'far_cliff', x: 52, z: -40, rot: -0.6, scale: 1.6, y: -3 },
    { kind: 'far_cliff', x: 2, z: -66, rot: 0, scale: 2.0, y: -4 },
  ] as SetPieceDef[],
};
const T5_LOOK: TownLook = {
  town: 5,
  liquid: 'oasis',
  liquidY: -0.9,
  floorTex: 'r1/tex/floor_dirt_arena.webp',
  floorKey: 'm',
  pano: 'village/tex/bg_village_panorama.webp',
  // 모래섬 마을: 걸을 수 있는 바닥과 2칸 이상 띄우고, 서문 바로 뒤(x < −34)는 비워 문이 샘물을 등지게 한다
  islands: [r(-46, -22, -32, -8), r(-44, 8, -30, 20), r(18, -26, 34, -14), r(28, -4, 42, 10), r(-30, 22, -14, 32), r(28, 20, 44, 32), r(-24, -36, -6, -26)],
  houses: { spacing: 8, h: [5.2, 6.6], tint: 0xf0dcb0 },
  cards: 'reed',
  drift: null,
  props: [
    // 샘 둘레 야자(청록 샘을 두른 네 그루) + 거리 야자
    { kind: 'tree', x: -6.5, z: -9.5, rot: 0.4, h: 6.6, tint: 0xeef0a0 },
    { kind: 'tree', x: 6.5, z: -9.5, rot: 1.9, h: 6.2, tint: 0xeef0a0 },
    { kind: 'tree', x: -6.5, z: 1.5, rot: 2.6, h: 6.0, tint: 0xeef0a0 },
    { kind: 'tree', x: 6.5, z: 1.5, rot: 0.9, h: 6.8, tint: 0xeef0a0 },
    { kind: 'tree', x: -13.5, z: -9.6, rot: 1.2, h: 6.4, tint: 0xeef0a0 },
    { kind: 'tree', x: 13.6, z: -9.6, rot: 2.2, h: 6.0, tint: 0xeef0a0 },
    { kind: 'tree', x: -9, z: -15.6, rot: 0.2, h: 6.2, tint: 0xeef0a0 },
    { kind: 'tree', x: 8.4, z: -15.6, rot: 1.5, h: 6.6, tint: 0xeef0a0 },
    // 물가의 야자(바닥 밖 호숫가)
    { kind: 'tree', x: -10, z: 21, rot: 0.8, h: 7.2, tint: 0xeef0a0, y: -0.9 },
    { kind: 'tree', x: 13, z: 20, rot: 2.4, h: 6.8, tint: 0xeef0a0, y: -0.9 },
    { kind: 'tree', x: -36, z: 6, rot: 1.1, h: 7.0, tint: 0xeef0a0, y: -0.9 },
    // 샘 한가운데 사암 상(옛 우물신)
    { kind: 'statue', x: 0, z: -4.5, rot: 0, h: 3.2, tint: 0xe8d4a0, y: -0.9 },
    // 거리 등불(금빛)
    { kind: 'v_lamppost', x: -7.2, z: 3.6, rot: 0, h: 3.6, tint: 0xf0c878 },
    { kind: 'v_lamppost', x: 7.2, z: 3.6, rot: 0, h: 3.6, tint: 0xf0c878 },
    { kind: 'v_lamppost', x: -8.4, z: -12.6, rot: 0, h: 3.6, tint: 0xf0c878 },
    { kind: 'v_lamppost', x: 8.4, z: -12.6, rot: 0, h: 3.6, tint: 0xf0c878 },
    { kind: 'v_lamppost', x: -17, z: -2.2, rot: 0, h: 3.6, tint: 0xf0c878 },
    { kind: 'v_lamppost', x: -29, z: 2.4, rot: 0, h: 3.6, tint: 0xf0c878 },
    { kind: 'v_lamppost', x: 7.4, z: 14.4, rot: 0, h: 3.6, tint: 0xf0c878 },
    // 문 앞 화로(서쪽 대상로 끝·남동 사구길 끝)
    { kind: 'brazier', x: -29.5, z: -2.5, rot: 0, h: 1.6 },
    { kind: 'brazier', x: -29.5, z: 2.5, rot: 0, h: 1.6 },
    { kind: 'brazier', x: 16.5, z: 23.5, rot: 0, h: 1.6 },
    { kind: 'brazier', x: 21.5, z: 23.5, rot: 0, h: 1.6 },
    // 대장간 마당 깃대(청록·금)
    { kind: 'village_banner_pole', x: -4.5, z: -15.6, rot: 0, h: 4.0, tint: 0x58d0c0 },
    { kind: 'village_banner_pole', x: 6.5, z: -12.2, rot: 0, h: 4.0, tint: 0xf0c050 },
    { kind: 'village_banner_pole', x: -23.2, z: -2.4, rot: E, h: 4.0, tint: 0xf0c050 },
    // 낙타 수레·짐(천막 곁)
    { kind: 'crates', x: 10.4, z: -3.6, rot: 0.3, h: 1.0, tint: 0xe8c890 },
    { kind: 'crates', x: 11.8, z: -2.8, rot: -0.2, h: 0.85, tint: 0xe8c890 },
    { kind: 'crates', x: -12.6, z: -3.6, rot: 0.5, h: 0.95, tint: 0xe8c890 },
    { kind: 'crates', x: 3.2, z: 12.4, rot: 0.2, h: 0.9, tint: 0xe8c890 },
    { kind: 'crates', x: 14, z: 10.6, rot: -0.4, h: 0.95, tint: 0xe8c890 },
    // 호수 바위(사암)
    { kind: 'rock', x: -12, z: 24, rot: 0.9, h: 2.4, tint: 0xd6b478, y: -0.9 },
    { kind: 'rock', x: 26, z: 18, rot: 2.1, h: 2.2, tint: 0xd6b478, y: -0.9 },
    { kind: 'rock', x: -26, z: 14, rot: 0.3, h: 1.9, tint: 0xd6b478, y: -0.9 },
  ],
  townSets: [],
  lights: [{ x: 0, y: 0.5, z: -4.5, s: 3.2 }, { x: -3.2, y: 0.3, z: -7, s: 1.6 }, { x: 3.4, y: 0.3, z: -1, s: 1.6 }],
  extras: [{ x: -3.4, z: 11.6, rot: 0.6 }],
};

// ════════════════════════════════════════════════════════
// ⑥ 하늘 요새 (3개월 C2)
// ════════════════════════════════════════════════════════
const T6_FLOORS = union(
  disk(0, -4, 8), // 수정 순간이동진 광장
  chamfer(r(-6, 2, 6, 18), 3, ['sw', 'se']), // 남쪽 성문 뜰(시작 자리)
  r(-24, -9, -7, 1), // 서쪽 대장간 테라스(끝에 엔드게임 준비 중 문, 정면 동쪽)
  r(7, -5, 30, 1), // 동쪽 비공정 부두
  r(30, -9, 40, 5), // 선착장(배가 묶인다)
  r(6, -30, 12, -5), // 북동 성채 길(끝에 심연의 성채 문, 정면 남쪽)
);
const T6_DOORS = doors(
  { gate: r(5, -31, 13, -30), exit: r(6, -30, 12, -27), to: 'r6', label: '심연의 성채' },
  { gate: r(-25, -9, -24, 1), exit: null, to: null, label: '엔드게임(준비 중)' },
);
const T6_LANDMARKS: Landmark[] = [
  { kind: 'forge', label: '대장간', rect: r(-20, -16, -12, -9), height: 6, solid: true }, // 0: 요새 대장간(서쪽 테라스 북쪽)
  { kind: 'shop', label: '상점', rect: r(-19, 1, -13, 7), height: 5, solid: true }, // 1: 보급 창고(서쪽 테라스 남쪽)
  { kind: 'trainer', label: '교관', rect: r(13, -12, 19, -5), height: 4, solid: true }, // 2: 정찰대 초소(부두 북쪽)
  { kind: 'board', label: '의뢰 게시판', rect: r(3, 12, 6, 13), height: 2.4, solid: true }, // 3
  ...T6_DOORS.landmarks, // 4·5
];
const T6: MapDef = {
  id: 'village',
  name: '하늘 요새',
  floors: T6_FLOORS,
  landmarks: T6_LANDMARKS,
  gates: [],
  rooms: [],
  exits: T6_DOORS.exits,
  spawn: TOWN_SPAWN,
  wallHeight: 2.5,
  // 가장자리: 요새 흰 돌 흉벽, 바깥은 구름 바다
  edgeStyle: 'parapet',
  sets: [
    // 원경: 구름 위로 솟은 보랏빛 성채 능선
    { kind: 'far_cliff', x: -50, z: -46, rot: 0.5, scale: 1.6, y: -6 },
    { kind: 'far_cliff', x: 52, z: -44, rot: -0.6, scale: 1.7, y: -6 },
    { kind: 'far_cliff', x: 0, z: -72, rot: 0, scale: 2.4, y: -8 },
  ] as SetPieceDef[],
};
const T6_LOOK: TownLook = {
  town: 6,
  liquid: 'cloud',
  liquidY: -2.6,
  floorTex: 'village/tex/plaza_cobble.webp',
  floorKey: 'b',
  pano: 'village/tex/bg_village_panorama.webp',
  // 구름 위에 뜬 작은 돌섬(집·망루): 걸을 수 있는 바닥과 2칸 이상 띄운다
  islands: [r(-44, -26, -30, -14), r(-44, 8, -30, 20), r(8, 16, 24, 28), r(-28, 22, -12, 34), r(44, -20, 56, -6), r(-8, -46, 6, -36)],
  houses: { spacing: 7.5, h: [5.0, 6.4], tint: 0xe8eef8 },
  cards: 'frost',
  drift: null,
  props: [
    // 수정 순간이동진: 가운데 수정 + 둘레 여섯 기둥
    { kind: 'statue', x: 0, z: -4.2, rot: 0, h: 3.4, tint: 0x9ee0ff },
    { kind: 'pillar', x: 4.8, z: -4, rot: 0, h: 2.0, tint: 0xd8f0ff },
    { kind: 'pillar', x: 2.4, z: 0.2, rot: 0, h: 2.0, tint: 0xd8f0ff },
    { kind: 'pillar', x: -2.4, z: 0.2, rot: 0, h: 2.0, tint: 0xd8f0ff },
    { kind: 'pillar', x: -4.8, z: -4, rot: 0, h: 2.0, tint: 0xd8f0ff },
    { kind: 'pillar', x: -2.4, z: -8.2, rot: 0, h: 2.0, tint: 0xd8f0ff },
    { kind: 'pillar', x: 2.4, z: -8.2, rot: 0, h: 2.0, tint: 0xd8f0ff },
    // 푸른 깃발(성채 길 양옆·서쪽 테라스 끝)
    { kind: 'banner', x: 6.8, z: -13, rot: 0, h: 4.2, tint: 0x6aa8ff },
    { kind: 'banner', x: 11.2, z: -13, rot: 0, h: 4.2, tint: 0x6aa8ff },
    { kind: 'banner', x: 6.8, z: -21, rot: 0, h: 4.2, tint: 0x6aa8ff },
    { kind: 'banner', x: 11.2, z: -21, rot: 0, h: 4.2, tint: 0x6aa8ff },
    { kind: 'village_banner_pole', x: -22.6, z: -6.4, rot: E, h: 4.2, tint: 0x6aa8ff },
    { kind: 'village_banner_pole', x: -22.6, z: -1.6, rot: E, h: 4.2, tint: 0x6aa8ff },
    // 대장간 화로
    { kind: 'brazier', x: -17.6, z: -8.4, rot: 0, h: 1.6 },
    { kind: 'brazier', x: -10.4, z: -8.4, rot: 0, h: 1.6 },
    // 가로등(흰 쇠)
    { kind: 'v_lamppost', x: -5.4, z: 4.4, rot: 0, h: 3.6, tint: 0xe8f0ff },
    { kind: 'v_lamppost', x: 5.4, z: 4.4, rot: 0, h: 3.6, tint: 0xe8f0ff },
    { kind: 'v_lamppost', x: -6, z: -8.6, rot: 0, h: 3.6, tint: 0xe8f0ff },
    { kind: 'v_lamppost', x: 26, z: -4.4, rot: 0, h: 3.6, tint: 0xe8f0ff },
    { kind: 'v_lamppost', x: 36.4, z: -6.6, rot: 0, h: 3.6, tint: 0xe8f0ff },
    // 선착장 화물
    { kind: 'crates', x: 33, z: -7, rot: 0.3, h: 1.1, tint: 0xe8eef8 },
    { kind: 'crates', x: 34.4, z: -6.2, rot: -0.2, h: 0.9, tint: 0xe8eef8 },
    { kind: 'crates', x: 37.6, z: 3.4, rot: 0.5, h: 1.0, tint: 0xe8eef8 },
    { kind: 'crates', x: 21, z: 0.2, rot: 0.2, h: 0.9, tint: 0xe8eef8 },
    { kind: 'crates', x: -7, z: 14.4, rot: 0.4, h: 0.95, tint: 0xe8eef8 },
  ],
  townSets: [
    // 비공정 선착장: 배가 부두 남쪽 구름에 묶여 떠 있다
    { kind: 'ship', x: 35, z: 9, rot: E, scale: 1.1, y: -0.4 },
    // 원경 보랏빛 성채(심연의 성채): 구름 속에서 솟은 탑들
    { kind: 'obsidian_spire', x: -16, z: -60, rot: 0.2, scale: 2.6, y: -8, tint: 0x8468b8, noLight: true },
    { kind: 'obsidian_spire', x: 6, z: -66, rot: -0.3, scale: 3.2, y: -9, tint: 0x7458a8, noLight: true },
    { kind: 'obsidian_spire', x: 26, z: -58, rot: 0.5, scale: 2.4, y: -8, tint: 0x8468b8, noLight: true },
    { kind: 'obsidian_spire', x: -36, z: -54, rot: 0.9, scale: 2.0, y: -7, tint: 0x8468b8, noLight: true },
  ],
  lights: [{ x: 0, y: 2.6, z: -4.2, s: 4.2 }, { x: 4.8, y: 2.2, z: -4, s: 1.2 }, { x: 2.4, y: 2.2, z: 0.2, s: 1.2 }, { x: -2.4, y: 2.2, z: 0.2, s: 1.2 }, { x: -4.8, y: 2.2, z: -4, s: 1.2 }, { x: -2.4, y: 2.2, z: -8.2, s: 1.2 }, { x: 2.4, y: 2.2, z: -8.2, s: 1.2 }],
  extras: [{ x: 3.4, z: 9.4, rot: -0.5 }],
};

/**
 * 걸을 수 있는 바닥 위에 놓인 키트 소품(화로·상자·가로등·계선주 등)은 1칸 충돌 사각형(kind 'prop')을 얻는다 — 영웅이 소품을 뚫고 지나가지 않게.
 * 렌더는 'prop' 랜드마크를 그리지 않는다(소품 목록이 그린다). 랜드마크 인덱스는 지도에 적은 것 뒤에 붙어 세트피스의 landmark 번호가 바뀌지 않는다
 */
function withPropSolids(map: MapDef, look: TownLook): MapDef {
  const walk = cellsOf(map.floors);
  const extra: Landmark[] = [];
  const seen = new Set<string>();
  for (const p of look.props) {
    if (p.y !== undefined) continue;
    const cx = Math.floor(p.x), cz = Math.floor(p.z), key = cx + ',' + cz;
    if (!walk.has(key) || seen.has(key)) continue;
    seen.add(key);
    extra.push({ kind: 'prop', rect: r(cx, cz, cx + 1, cz + 1), height: p.h, solid: true });
  }
  return { ...map, landmarks: [...map.landmarks, ...extra] };
}

/** 마을②~⑥ 지도(levels.VILLAGES가 마을① 뒤에 잇는다) */
export const TOWN_MAPS: readonly MapDef[] = [withPropSolids(T2, T2_LOOK), withPropSolids(T3, T3_LOOK), withPropSolids(T4, T4_LOOK), withPropSolids(T5, T5_LOOK), withPropSolids(T6, T6_LOOK)];
/** 마을 번호 → 렌더 풍경(마을①은 없다: 기존 EnvVisual) */
export const TOWN_LOOKS: Readonly<Record<number, TownLook>> = { 2: T2_LOOK, 3: T3_LOOK, 4: T4_LOOK, 5: T5_LOOK, 6: T6_LOOK };
/** 지도 객체 → 마을 번호(2..6). 마을①·던전 지도는 0 */
export function townNoOfMap(map: MapDef): number {
  const i = TOWN_MAPS.indexOf(map);
  return i < 0 ? 0 : i + 2;
}
